import { Component, ReactNode } from "react";

import * as pbi from "powerbi-client";

import { BiReport } from "../../../models/BiReport";
import { IEmbedConfiguration } from "embed";
import { models, Report } from "powerbi-client";
import { IBasicFilter, BasicFilterOperators } from "powerbi-models";
import { BiBasicFilter } from "../../../models/BiBasicFilter";
import { BiReportPageSettings } from "../../../models/BiReportPageSettings";
import { IError } from "powerbi-models";
import { ScreenCloudLogger } from "../../../ScreenCloudReactApp";

interface Props {
  urlFilter?: string;
  report: BiReport;
  filters: BiBasicFilter[];
  pageSettings: BiReportPageSettings;
  appStarted?: boolean;
  reloadCount: number;
  sendErrorToSentry: (error: string, detailedError?: string) => void;
  logger: ScreenCloudLogger | undefined;
}

export class ReportRenderer extends Component<Props, {}> {
  private timerId?: number;
  private powerbi?: pbi.service.Service;
  private report?: Report;
  private currentPage = 1;
  private filters: IBasicFilter[];

  constructor(props: Props) {
    super(props);
    this.filters =
      props.report.type === "report"
        ? this.createFilters(this.props.filters)
        : [];
  }

  startPageTimeTimer = (): void => {
    if (
      this.report &&
      this.props.pageSettings !== undefined &&
      this.props.pageSettings.pageChange
    ) {
      const timeoutPeriod =
        this.props.pageSettings.pageTime === undefined ||
        this.props.pageSettings.pageTime < 30
          ? 30000
          : this.props.pageSettings.pageTime * 1000;

      // make extra sure no other timer set
      if (this.timerId) {
        console.log("Clearing Timeout " + this.timerId + " :" + new Date());
        clearTimeout(this.timerId);
      }

      this.timerId = window.setTimeout(async () => {
        if (!this.report) {
          console.error("Report was previously defined, but is not anymore.");
          this.props.logger?.error(
            `Report was previously defined, but is not anymore.`
          );
          return;
        }

        let pos = this.currentPage;
        pos = pos += 1;
        const pages = await this.report.getPages();
        const page = pages[this.currentPage];

        if (pos >= pages.length) {
          pos = 0;
        }
        this.currentPage = pos;
        if (page) {
          await page.setActive();
          await this.startPageTimeTimer();
        }
      }, timeoutPeriod);
    }

    console.log("Setting Timeout " + this.timerId + " : " + new Date());
  };

  tryToStartTimer = async (): Promise<void> => {
    if (
      this.props.report.type === "report" &&
      this.props.pageSettings !== undefined &&
      this.props.pageSettings.pageChange === true &&
      !this.timerId &&
      this.props.appStarted
    ) {
      await this.startPageTimeTimer();
    }
  };

  createFilters = (filterProps: BiBasicFilter[]): IBasicFilter[] => {
    const filters: IBasicFilter[] = [];
    filterProps.forEach((element) => {
      let op: BasicFilterOperators = "In";
      if (element.operator === "NotIn") {
        op = "NotIn";
      } else if (element.operator === "All") {
        op = "All";
      }
      filters.push({
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
          table: element.table,
          column: element.column,
        },
        filterType: models.FilterType.Basic,
        operator: op,
        values: element.values,
      });
    });

    return filters;
  };

  // this is async so we have to be careful when comes to setting timers
  async componentDidUpdate(prevProps: Props): Promise<void> {
    // if just the auth token has changed
    if (
      this.report &&
      this.props.report.token !== prevProps.report.token &&
      this.props.report.embeddedUrl === prevProps.report.embeddedUrl &&
      this.props.report.reportId === prevProps.report.reportId &&
      this.props.report.workspaceId === prevProps.report.workspaceId
    ) {
      await this.report.setAccessToken(this.props.report.token);
      return;
    }

    if (
      this.props.report.type === "report" &&
      (prevProps.report !== this.props.report ||
        prevProps.filters !== this.props.filters)
    ) {
      // clear timer
      if (this.timerId) {
        console.log(
          "DidUpdate Clearing Timeout " + this.timerId + " :" + new Date()
        );
        clearTimeout(this.timerId);
      }

      this.filters = this.createFilters(this.props.filters);

      this.currentPage = 1;
      await this.renderReport();

      if (
        this.props.pageSettings !== undefined &&
        this.props.pageSettings.pageChange === true
      ) {
        await this.startPageTimeTimer();
      }
    }

    // if app is now just starting get the timer going
    if (!prevProps.appStarted && this.props.appStarted) {
      await this.tryToStartTimer();
    }

    // if we have to reload the report
    if (this.report && this.props.reloadCount > prevProps.reloadCount) {
      console.log("DidUpdate Reloading report: " + new Date());
      await this.report.refresh();
    }
  }

  async componentDidMount(): Promise<void> {
    await this.renderReport();
    await this.tryToStartTimer();
  }

  renderReport = (): void => {
    console.log("Rendering Report : " + new Date());
    const embedUrl = this.props.urlFilter
      ? this.props.report.embeddedUrl + "&filter=" + this.props.urlFilter.trim()
      : this.props.report.embeddedUrl;

    const settings = {
      filterPaneEnabled: false,
      navContentPaneEnabled: false,
    };

    // Create the Config
    const config: IEmbedConfiguration = {
      type: this.props.report.type,
      tokenType: models.TokenType.Aad,
      accessToken: this.props.report.token,
      embedUrl,
      id: this.props.report.reportId,
      permissions: models.Permissions.Read,
      settings: settings,
      filters: this.filters,
    };

    // Create Power Bi Service
    console.log("Start : Creating MS Power Bi Service " + new Date());
    this.powerbi = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory
    );

    console.log("End : Creating MS Power Bi Service " + new Date());

    // Capture where Power Bi is to be rendered
    const reportContainer = document.getElementById("reportContainer");

    if (!reportContainer) {
      this.props.sendErrorToSentry(
        "Could not update report, no #reportContainer HTML element found."
      );
      //Datadog Log
      this.props.logger?.error(
        `Could not update report, no #reportContainer HTML element found.`
      );
      return;
    }

    this.powerbi.reset(reportContainer);

    // Render Power Bi
    console.log("Start : Embedding MS Power Bi Report " + new Date());

    this.report = this.powerbi.load(reportContainer, config) as Report;

    console.log("End : Embedding MS Power Bi Report " + new Date());

    this.report.on<IError>("error", (e) => {
      console.log("MS BI Report Error " + new Date(), e.detail);
      this.props.sendErrorToSentry(e.detail.message, e.detail.detailedMessage);

      //Datadog Log
      this.props.logger?.error(`MS BI Report Error ${e.detail.message}`, {
        defailedMessage: e.detail.detailedMessage,
      });
    });

    this.report.on("loaded", async (e) => {
      if (
        this.report &&
        this.report.iframe &&
        this.report.iframe.contentWindow &&
        this.props.report.type === "report" &&
        (!this.props.pageSettings || !this.props.pageSettings.pageChange) &&
        this.props.pageSettings.pageName
      ) {
        console.log("MS BI Report Loaded " + new Date(), e.detail);

        const pages = await this.report.getPages();

        const pagesToDelete = pages.filter(
          (p) => p.displayName !== this.props.pageSettings.pageName
        );

        for (let i = 0; i < pagesToDelete.length; i++) {
          console.log(`Removing Page: ${pagesToDelete[i].displayName}`);
          await pagesToDelete[i].delete();
        }

        console.log(`Displaying Page: ${this.props.pageSettings.pageName}`);

        this.report.render();
      } else {
        if (this.report) {
          this.report.render();
        }
      }
    });
  };

  componentWillUnmount(): void {
    if (this.timerId) {
      console.log(
        "Unmounting Clearing Timeout " + this.timerId + " :" + new Date()
      );
      clearTimeout(this.timerId);
    }

    if (this.report) {
      this.report.off("loaded");
      this.report.off("error");
    }
  }

  render(): ReactNode {
    return (
      <div>
        <div id="reportContainer" className="power-bi-report-holder"></div>
      </div>
    );
  }
}
